import { useMemo, useState } from 'react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { crm2Api, useCrm2Calendar, useCrm2Meta, useCrm2Slots, useCrm2Write, type Booking } from './api'
import { Field, fieldClass, Section } from './shared'

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
const ZONES = ['Asia/Manila', 'Asia/Kolkata', 'Asia/Singapore', 'Asia/Jakarta', 'Asia/Dubai', 'Australia/Sydney', 'Europe/London', 'America/New_York', 'America/Los_Angeles', 'Africa/Johannesburg', 'UTC']
const hhmm = (m: number) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`
const mins = (v: string) => {
  const [h, m] = v.split(':').map(Number)
  return (h ?? 0) * 60 + (m ?? 0)
}

/**
 * The demo booking calendar: who takes demos, the team's weekly hours in one time zone, and the
 * rules the website and Outreach book within. Edited as a whole and saved with one button; the
 * parent keys it on the saved value, so a save elsewhere starts it afresh.
 */
export function BookingSettingsSection({ value, canEdit }: { value: Booking; canEdit: boolean }) {
  const [b, setB] = useState<Booking>(value)
  const people = useCrm2Meta().data?.people ?? []
  const save = useCrm2Write((v: Partial<Booking>) => crm2Api.saveSettings({ booking: v }), () => 'Demo booking saved')
  const [weekFrom] = useState(() => new Date().toISOString())
  const weekTo = useMemo(() => new Date(Date.parse(weekFrom) + 7 * 86_400_000).toISOString(), [weekFrom])
  const status = useCrm2Calendar(weekFrom, weekTo)
  const preview = useCrm2Slots(weekFrom, weekTo, value.memberIds.length > 0)
  const set = <K extends keyof Booking>(k: K, v: Booking[K]) => setB((cur) => ({ ...cur, [k]: v }))
  const dirty = JSON.stringify(b) !== JSON.stringify(value)
  const problem = (id: string) => status.data?.team.find((h) => h.id === id)?.problem

  const num = (k: 'durationMin' | 'bufferMin' | 'minNoticeHours' | 'daysAhead' | 'maxPerDay', label: string, hint: string, min: number, max: number) => (
    <Field label={label} hint={hint}>
      <Input type="number" min={min} max={max} value={b[k]} disabled={!canEdit} onChange={(e) => set(k, Math.max(min, Math.min(max, Number(e.target.value) || 0)))} />
    </Field>
  )

  return (
    <Section title="Demo booking">
      <div className="grid gap-4 px-4 py-3 text-sm">
        <div className="flex items-center gap-3">
          <Switch checked={b.enabled} disabled={!canEdit} onCheckedChange={(v) => set('enabled', v)} aria-label="Website booking on" />
          <span>{b.enabled ? 'The website shows open times to pick from' : 'The website shows the request form only'}</span>
        </div>

        <div className="grid gap-2">
          <div className="text-xs font-medium text-muted-foreground">Who takes demos (round robin: whoever is free and has had fewest lately)</div>
          <div className="grid gap-1.5 sm:grid-cols-2">
            {people.map((p) => {
              const on = b.memberIds.includes(p.id)
              const why = on ? problem(p.id) : null
              return (
                <label key={p.id} className="flex items-start gap-2">
                  <input type="checkbox" className="mt-1" checked={on} disabled={!canEdit} onChange={(e) => set('memberIds', e.target.checked ? [...b.memberIds, p.id] : b.memberIds.filter((x) => x !== p.id))} />
                  <span>
                    {p.name}
                    {why ? <span className="block text-tiny text-warning">{why}</span> : on && status.data ? <span className="block text-tiny text-success">Can be booked</span> : null}
                  </span>
                </label>
              )
            })}
          </div>
          <p className="text-tiny text-text-muted">Each person connects Google Calendar from their profile menu, with editing allowed. Demos go on their calendar with a Meet link, and only into times they are free.</p>
        </div>

        <div className="grid gap-3 sm:grid-cols-3">
          <Field label="Invitation title" hint="The company name is added after it.">
            <Input value={b.title} maxLength={80} disabled={!canEdit} onChange={(e) => set('title', e.target.value)} />
          </Field>
          <Field label="Time zone of the hours below">
            <input list="booking-zones" className={fieldClass} value={b.timezone} disabled={!canEdit} onChange={(e) => set('timezone', e.target.value)} />
            <datalist id="booking-zones">
              {ZONES.map((z) => (
                <option key={z} value={z} />
              ))}
            </datalist>
          </Field>
          <Field label="Offer a start every">
            <select className={fieldClass} value={b.stepMin} disabled={!canEdit} onChange={(e) => set('stepMin', Number(e.target.value) as Booking['stepMin'])}>
              {[15, 30, 45, 60].map((n) => (
                <option key={n} value={n}>
                  {n} minutes
                </option>
              ))}
            </select>
          </Field>
          {num('durationMin', 'Demo length (minutes)', '45 by default.', 15, 180)}
          {num('bufferMin', 'Free time before and after (minutes)', 'Kept clear around every demo.', 0, 120)}
          {num('minNoticeHours', 'Minimum notice (hours)', 'For the website. Outreach can book sooner.', 0, 168)}
          {num('daysAhead', 'Bookable up to (days ahead)', 'For the website.', 1, 60)}
          {num('maxPerDay', 'Demos per person per day', '0 means no limit.', 0, 20)}
        </div>

        <div className="grid gap-1.5">
          <div className="text-xs font-medium text-muted-foreground">Weekly hours</div>
          {DAYS.map((d, i) => {
            const w = b.hours[i]?.[0] ?? null
            const extra = (b.hours[i]?.length ?? 0) > 1
            const put = (next: Array<[number, number]>) => set('hours', b.hours.map((x, k) => (k === i ? next : x)))
            return (
              <div key={d} className="grid grid-cols-[7rem_auto_minmax(0,1fr)] items-center gap-2">
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={Boolean(w)} disabled={!canEdit} onChange={(e) => put(e.target.checked ? [[540, 1080]] : [])} />
                  {d}
                </label>
                {w ? (
                  <span className="flex items-center gap-1">
                    <input type="time" step={900} className={cn(fieldClass, 'w-28')} value={hhmm(w[0])} disabled={!canEdit} onChange={(e) => put([[mins(e.target.value), w[1]], ...(b.hours[i]?.slice(1) ?? [])])} />
                    <span className="text-muted-foreground">to</span>
                    <input type="time" step={900} className={cn(fieldClass, 'w-28')} value={hhmm(w[1])} disabled={!canEdit} onChange={(e) => put([[w[0], mins(e.target.value)], ...(b.hours[i]?.slice(1) ?? [])])} />
                  </span>
                ) : (
                  <span className="text-muted-foreground">No demos</span>
                )}
                <span className="text-tiny text-text-muted">{extra ? 'Has more than one window; the others are kept.' : w && w[1] <= w[0] ? 'The end has to be after the start.' : ''}</span>
              </div>
            )
          })}
        </div>

        <Field label="Added to the invitation" hint="What to prepare, who will join, a link to read before the call.">
          <Textarea rows={3} maxLength={2000} value={b.description} disabled={!canEdit} onChange={(e) => set('description', e.target.value)} />
        </Field>

        <div className="flex flex-wrap items-center gap-3">
          <Button size="sm" disabled={!canEdit || !dirty || save.isPending || b.hours.some((d) => d.some(([s, e]) => e <= s))} onClick={() => save.mutate(b)}>
            {save.isPending ? 'Saving…' : 'Save demo booking'}
          </Button>
          {dirty ? (
            <Button size="sm" variant="ghost" onClick={() => setB(value)}>
              Undo changes
            </Button>
          ) : null}
          <span className="text-xs text-muted-foreground">
            {!value.memberIds.length ? 'Add people to start offering times.' : preview.data ? `${preview.data.slots.length} open times in the next 7 days, as saved.` : preview.isPending ? 'Counting open times…' : ''}
          </span>
        </div>
      </div>
    </Section>
  )
}
